import React, { useCallback, useState } from 'react'
import { Cascader, Form, Image, Input, Modal, Spin, Switch } from 'antd'
import { useRequest } from 'ahooks'
import copy from 'copy-to-clipboard'
import Button from '@/components/button/button'
import SgUpload from '@/components/upload/upload'
import locationImg from '@/assets/images/location.svg'
import { getQiniuImgUrl } from '@/config/qiniu'
import $msg from '@/utils/message'
import { IShopInfo } from '@/model/types/shop'
import { shopStore } from '@/store/shop'
import domain from '@/api/config'
import SettingPage, { SettingGrid, SettingPanel } from '@/views/main/modules/member-manage/member-setting/setting-page'
import { editShopInfo, getShopInfo } from '../api'
import MapPicker, { BAIDU_MAP_KEY, MapPoint } from './map-picker'
import { getRegionNames, regionOptions } from './shop-address'

import './shop-info.scss'

const AUTH_STATUS_TEXT: Record<number, string> = {
    [-1]: '未认证',
    0: '审核中',
    1: '已认证',
    2: '已拒绝',
}

const AUTH_TYPE_TEXT: Record<number, string> = {
    0: '企业认证',
    1: '个人认证',
    2: '小微商户',
}

const CHANNEL_NAMES: Record<number, string> = { 0: '扫呗', 1: '商米', 2: '收钱吧', 3: '建行' }
const CHANNEL_STATUS: Record<number, string> = { [-1]: '未申请', 0: '审核中', 1: '已开通', 2: '已拒绝' }
const SHOP_VERSIONS: Record<number, string> = { 0: '标准版', 1: '专业版', 2: '旗舰版' }

const centerUrl = (path: string, params: Record<string, string | number | undefined>) => {
    const query = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
        if (value != null) query.set(key, String(value))
    })

    return `${domain.center}/#/main/${path}?${query.toString()}`
}

type EditField = 'shop_name' | 'phone' | 'description' | 'address' | null

interface InfoRowProps {
    label: string
    children: React.ReactNode
    actionText?: string
    onAction?: () => void
    action?: React.ReactNode
}

const InfoRow: React.FC<InfoRowProps> = props => (
    <div className="store-setting-page__row">
        <span className="store-setting-page__label">{props.label}</span>
        <div className="store-setting-page__value">{props.children}</div>
        {(props.actionText || props.action) && (
            <div className="store-setting-page__action">
                {props.action || (
                    <Button types="text" onClick={props.onAction}>
                        {props.actionText}
                    </Button>
                )}
            </div>
        )}
    </div>
)

const ShopInfo: React.FC = () => {
    const [shopInfo, setShopInfo] = useState<IShopInfo>()
    const [editField, setEditField] = useState<EditField>(null)
    const [saving, setSaving] = useState(false)
    const [logoSaving, setLogoSaving] = useState(false)
    const [loadFailed, setLoadFailed] = useState(false)
    const [showMap, setShowMap] = useState(false)
    const [mapPoint, setMapPoint] = useState<MapPoint | null>(null)
    const [showBusinessSaving, setShowBusinessSaving] = useState(false)
    const [licenseSaving, setLicenseSaving] = useState(false)
    const [permissionSaving, setPermissionSaving] = useState(false)
    const [form] = Form.useForm<{ shop_name: string; phone: string; description: string; detailed_address: string }>()
    const [regionCodes, setRegionCodes] = useState<string[]>([])
    // 地图回填时递增，用于强制重挂载详细地址表单项，避免表单 store 已更新但字段未重渲染
    const [addressFieldKey, setAddressFieldKey] = useState(0)

    const { loading, run: load } = useRequest(getShopInfo, {
        onSuccess: res => {
            setShopInfo(res)
            shopStore.setShopInfo(res)
            setLoadFailed(false)
        },
        onError: (e: any) => {
            setLoadFailed(true)
            $msg('error', e.message || '获取店铺信息失败')
        },
    })

    const save = async () => {
        try {
            const values = await form.validateFields()
            setSaving(true)
            const payload: Record<string, any> = {}
            if (editField === 'address') {
                if (regionCodes.length > 0 && regionCodes.length < 3) {
                    $msg('warning', '请选择完整的省、市、区')

                    return
                }
                payload.detailed_address = values.detailed_address
                payload.district_code = regionCodes.length
                    ? Number(regionCodes[regionCodes.length - 1])
                    : shopInfo?.district_code
                if (!mapPoint) {
                    $msg('warning', '请在地图中重新选择位置，避免地址与地图坐标不一致')

                    return
                }
                payload.longitude = Number(mapPoint.longitude.toFixed(6))
                payload.latitude = Number(mapPoint.latitude.toFixed(6))
            } else if (editField) {
                payload[editField] = values[editField]
            }
            await editShopInfo(payload)
            $msg('success', '修改成功')
            setEditField(null)
            load()
        } catch (e) {
            if (e && e.errorFields) return
            $msg('error', e.message || '修改失败')
        } finally {
            setSaving(false)
        }
    }

    const uploadLogo = useCallback(
        (res: any) => {
            const key = res && (res.key || res.hash)
            if (!key) {
                setLogoSaving(false)

                return
            }
            editShopInfo({ logo_image_url: key })
                .then(() => {
                    $msg('success', '修改成功')
                    load()
                })
                .catch((e: any) => $msg('error', e.message || '修改失败'))
                .finally(() => setLogoSaving(false))
        },
        [load],
    )

    const openEdit = (field: EditField) => {
        if (!shopInfo) return
        if (field === 'address') {
            form.setFieldsValue({ detailed_address: shopInfo.detailed_address })
            setRegionCodes(
                [shopInfo.province_code, shopInfo.city_code, shopInfo.district_code].filter(Boolean).map(String),
            )
            setMapPoint(null)
        } else if (field === 'description') {
            form.setFieldsValue({ description: (shopInfo as any).description })
        } else if (field) {
            form.setFieldsValue({ [field]: (shopInfo as any)[field] } as any)
        }
        setEditField(field)
    }

    const editTitle: Record<Exclude<EditField, null>, string> = {
        shop_name: '修改店铺名',
        phone: '修改联系电话',
        description: '修改店铺简介',
        address: '修改店铺地址',
    }

    const authText = shopInfo
        ? shopInfo.auth_status === 1
            ? `${AUTH_TYPE_TEXT[shopInfo.auth_type] || ''} ${shopInfo.auth_name || ''}`.trim()
            : AUTH_STATUS_TEXT[shopInfo.auth_status] || '未认证'
        : ''

    const regionNames = shopInfo
        ? getRegionNames([shopInfo.province_code, shopInfo.city_code, shopInfo.district_code])
        : []
    const fullAddress = [...regionNames, shopInfo?.detailed_address].filter(Boolean).join(' ') || '--'
    const shopPoint =
        shopInfo?.longitude && shopInfo?.latitude
            ? { longitude: Number(shopInfo.longitude), latitude: Number(shopInfo.latitude) }
            : null
    const shopMapPreview = shopPoint
        ? `https://api.map.baidu.com/staticimage/v2?${new URLSearchParams({
              ak: BAIDU_MAP_KEY,
              center: `${shopPoint.longitude},${shopPoint.latitude}`,
              markers: `${shopPoint.longitude},${shopPoint.latitude}`,
              width: '120',
              height: '40',
              zoom: '17',
          })}`
        : ''

    const centerParams = shopInfo
        ? {
              shop_id: shopInfo.id,
              shop_type: 'new_ls',
              shop_name: shopInfo.shop_name,
              shop_super_admin_passport_id: shopInfo.creator?.passport_id,
          }
        : null

    const openCenter = (path: string, params: Record<string, string | number | undefined> = {}) => {
        if (!centerParams || !domain.center) return
        window.location.href = centerUrl(path, { ...centerParams, ...params })
    }

    const openAuth = () => {
        if (!shopInfo || shopInfo.shop_type === 1) return
        openCenter(shopInfo.auth_status === -1 ? 'shopApply' : 'shopApplyStatus')
    }

    const openPayment = () => {
        if (!shopInfo) return
        if (shopInfo.shop_type === 1 && shopInfo.chain_shop?.online_payment === 0) {
            $msg('warning', '直营店统一入账到连锁总部账户，请联系总部开通')

            return
        }
        if (shopInfo.shop_type !== 1 && shopInfo.auth_status !== 1 && shopInfo.online_payment !== 4) {
            Modal.confirm({
                title: '认证提示',
                content: '已认证店铺才可申请开通在线支付，请先完成店铺认证。',
                okText: '去认证',
                cancelText: '取消',
                onOk: openAuth,
            })

            return
        }
        openCenter('paymentApprovedLoading', {
            paymentType: shopInfo.online_payment !== 0 || (shopInfo.pay_channel || []).length ? 'change' : undefined,
            is_sunmi: shopInfo.from_sunmi ? 1 : 0,
            is_chain: shopInfo.chain_shop ? 1 : 0,
        })
    }

    const updateBusiness = async (checked: boolean) => {
        setShowBusinessSaving(true)
        try {
            await editShopInfo({ show_bussiness: checked })
            load()
            $msg('success', '修改成功')
        } catch (error) {
            $msg('error', error.message || '修改失败')
        } finally {
            setShowBusinessSaving(false)
        }
    }

    const uploadCredential = (field: 'bussiness_license_url' | 'bussiness_permission_url', result: any) => {
        const key = result?.key || result?.hash
        if (!key) return
        editShopInfo({ [field]: key })
            .then(() => {
                load()
                $msg('success', '上传成功')
            })
            .catch((error: Error) => $msg('error', error.message || '保存失败'))
            .finally(() => (field === 'bussiness_license_url' ? setLicenseSaving(false) : setPermissionSaving(false)))
    }

    const activeChannel = shopInfo?.pay_channel?.find(channel => channel.channel_type === shopInfo.online_payment - 1)
    const paymentDetail =
        shopInfo?.shop_type === 1 && shopInfo.chain_shop
            ? shopInfo.chain_shop.online_payment
                ? '总部已开通'
                : '总部未开通'
            : activeChannel
            ? `${CHANNEL_NAMES[activeChannel.channel_type] || '支付'}渠道（${
                  CHANNEL_STATUS[activeChannel.status] || '未知状态'
              }）`
            : shopInfo?.online_payment
            ? '已开通'
            : '未开通'

    const payText = shopInfo ? (shopInfo.online_payment === 0 ? '未开通' : '已开通') : '--'

    return (
        <SettingPage title="店铺信息" className="store-setting-page setting-info">
            <div className="store-setting-page__heading">
                <h1 className="store-setting-page__title">店铺信息</h1>
                <p className="store-setting-page__subtitle">查看店铺资料、认证及在线支付状态。</p>
            </div>
            <Spin spinning={loading}>
                {loadFailed && !shopInfo ? (
                    <div className="store-setting-page__error" role="alert">
                        店铺信息加载失败。
                        <Button types="text" onClick={() => load()}>
                            重新加载
                        </Button>
                    </div>
                ) : (
                    <SettingGrid>
                        <SettingPanel title="店铺概览" wide>
                            <div className="setting-info__overview">
                                {shopInfo?.logo_image_url ? (
                                    <Image
                                        width={72}
                                        height={72}
                                        className="setting-info__logo"
                                        src={getQiniuImgUrl(shopInfo.logo_image_url, 200, 200)}
                                    />
                                ) : (
                                    <div className="setting-info__logo-placeholder">店</div>
                                )}
                                <div className="setting-info__identity">
                                    <div className="setting-info__name">{shopInfo?.shop_name || '--'}</div>
                                    <div className="setting-info__shop-number">
                                        店铺号：{shopInfo?.number || '--'}
                                        {shopInfo?.number && (
                                            <Button
                                                types="text"
                                                onClick={() => {
                                                    copy(String(shopInfo.number))
                                                    $msg('success', '店铺号已复制')
                                                }}
                                            >
                                                复制
                                            </Button>
                                        )}
                                    </div>
                                    <div className="setting-info__status">
                                        <span
                                            className={`store-setting-page__status${
                                                shopInfo?.auth_status === 1 ? ' store-setting-page__status--active' : ''
                                            }`}
                                        >
                                            {authText || '--'}
                                        </span>
                                        <span
                                            className={`store-setting-page__status${
                                                shopInfo?.online_payment !== 0 && shopInfo
                                                    ? ' store-setting-page__status--active'
                                                    : ''
                                            }`}
                                        >
                                            在线支付{payText}
                                        </span>
                                    </div>
                                </div>
                                <SgUpload
                                    className={logoSaving ? 'store-setting-page__upload--disabled' : ''}
                                    accept="image/*"
                                    onLoad={() => setLogoSaving(true)}
                                    onError={() => setLogoSaving(false)}
                                    onSuccess={uploadLogo}
                                >
                                    <Button types="text" loading={logoSaving} disabled={logoSaving}>
                                        更换 Logo
                                    </Button>
                                </SgUpload>
                            </div>
                        </SettingPanel>
                        <SettingPanel title="基本资料" wide>
                            <InfoRow label="店铺名称" actionText="修改" onAction={() => openEdit('shop_name')}>
                                {shopInfo?.shop_name || '--'}
                            </InfoRow>
                            <InfoRow label="联系电话" actionText="修改" onAction={() => openEdit('phone')}>
                                {shopInfo?.phone || '--'}
                            </InfoRow>
                            <InfoRow label="店铺地址" actionText="修改" onAction={() => openEdit('address')}>
                                <div className="setting-info__address-value">
                                    <button
                                        className="setting-info__map-preview"
                                        type="button"
                                        aria-label="在地图上查看或选择店铺地址"
                                        onClick={() => {
                                            openEdit('address')
                                            setShowMap(true)
                                        }}
                                    >
                                        {shopPoint ? <img src={shopMapPreview} alt="店铺位置" /> : <span>地图</span>}
                                        <img className="setting-info__map-pin" src={locationImg} alt="" />
                                    </button>
                                    <span>{fullAddress}</span>
                                </div>
                            </InfoRow>
                            <InfoRow label="店铺简介" actionText="修改" onAction={() => openEdit('description')}>
                                <span className="setting-info__description">{shopInfo?.description || '--'}</span>
                            </InfoRow>
                        </SettingPanel>
                        <SettingPanel title="认证与支付" wide>
                            <InfoRow
                                label="店铺认证"
                                actionText={
                                    shopInfo?.shop_type === 1
                                        ? undefined
                                        : shopInfo?.auth_status === -1
                                        ? '去认证'
                                        : '查看'
                                }
                                onAction={openAuth}
                            >
                                <span
                                    className={`store-setting-page__status${
                                        shopInfo?.auth_status === 1 ? ' store-setting-page__status--active' : ''
                                    }`}
                                >
                                    {shopInfo?.shop_type === 1 && shopInfo.chain_shop
                                        ? shopInfo.chain_shop.auth_status === 1
                                            ? '总店已认证'
                                            : '总店未认证，请联系总店认证'
                                        : authText || '--'}
                                </span>
                            </InfoRow>
                            <InfoRow
                                label="在线支付"
                                actionText={shopInfo?.online_payment ? '查看／更换通道' : '申请开通'}
                                onAction={openPayment}
                            >
                                <span
                                    className={`store-setting-page__status${
                                        shopInfo?.online_payment !== 0 && shopInfo
                                            ? ' store-setting-page__status--active'
                                            : ''
                                    }`}
                                >
                                    {paymentDetail}
                                </span>
                                {shopInfo?.online_payment === 0 && (
                                    <div className="store-setting-page__hint">开通后可使用微信、支付宝条码收款。</div>
                                )}
                            </InfoRow>
                        </SettingPanel>
                        <SettingPanel title="资质证件" wide>
                            <InfoRow
                                label="营业执照"
                                action={
                                    shopInfo?.auth_status === 1 && (
                                        <SgUpload
                                            images
                                            onLoad={() => setLicenseSaving(true)}
                                            onError={() => setLicenseSaving(false)}
                                            onSuccess={result => uploadCredential('bussiness_license_url', result)}
                                        >
                                            <Button types="text" loading={licenseSaving} disabled={licenseSaving}>
                                                {shopInfo.bussiness_license_url ? '修改营业执照' : '上传营业执照'}
                                            </Button>
                                        </SgUpload>
                                    )
                                }
                            >
                                {shopInfo?.bussiness_license_url ? (
                                    <Image
                                        width={64}
                                        height={64}
                                        src={getQiniuImgUrl(shopInfo.bussiness_license_url, 180, 180)}
                                    />
                                ) : (
                                    '--'
                                )}
                            </InfoRow>
                            <InfoRow
                                label="经营许可证"
                                action={
                                    shopInfo?.auth_status === 1 && (
                                        <SgUpload
                                            images
                                            onLoad={() => setPermissionSaving(true)}
                                            onError={() => setPermissionSaving(false)}
                                            onSuccess={result => uploadCredential('bussiness_permission_url', result)}
                                        >
                                            <Button types="text" loading={permissionSaving} disabled={permissionSaving}>
                                                {shopInfo.bussiness_permission_url
                                                    ? '修改经营许可证'
                                                    : '上传经营许可证'}
                                            </Button>
                                        </SgUpload>
                                    )
                                }
                            >
                                {shopInfo?.bussiness_permission_url ? (
                                    <Image
                                        width={64}
                                        height={64}
                                        src={getQiniuImgUrl(shopInfo.bussiness_permission_url, 180, 180)}
                                    />
                                ) : (
                                    '--'
                                )}
                            </InfoRow>
                            <InfoRow label="在微商城展示证件">
                                <Switch
                                    checked={!!shopInfo?.show_bussiness}
                                    disabled={
                                        showBusinessSaving ||
                                        (!shopInfo?.bussiness_license_url && !shopInfo?.bussiness_permission_url)
                                    }
                                    loading={showBusinessSaving}
                                    onChange={updateBusiness}
                                />
                            </InfoRow>
                        </SettingPanel>
                        <SettingPanel title="店铺与服务" wide>
                            <InfoRow label="创建人">
                                {shopInfo?.creator
                                    ? `${shopInfo.creator.realname || shopInfo.creator.nickname || '--'}（超管）`
                                    : '--'}
                            </InfoRow>
                            <InfoRow label="创建时间">{shopInfo?.create_time || '--'}</InfoRow>
                            <InfoRow
                                label="付费中心"
                                actionText="查看付费记录"
                                onAction={() => openCenter('recordViews')}
                            >
                                查看店铺付费记录
                            </InfoRow>
                            <InfoRow label="当前版本">
                                {shopInfo
                                    ? shopInfo.use_status === 0
                                        ? '试用版'
                                        : SHOP_VERSIONS[shopInfo.shop_version] || '--'
                                    : '--'}
                                {shopInfo?.remain_days != null
                                    ? `，${shopInfo.remain_days >= 0 ? `${shopInfo.remain_days} 天后到期` : '已到期'}`
                                    : ''}
                            </InfoRow>
                            <InfoRow
                                label="客户端下载"
                                actionText="前往下载"
                                onAction={() =>
                                    window.open(
                                        `${process.env.REACT_APP_SERVER_DOMAIN}/#/downloadNavigation`,
                                        '_blank',
                                        'noopener,noreferrer',
                                    )
                                }
                            >
                                森果收银台 / 森果零售通
                            </InfoRow>
                        </SettingPanel>
                    </SettingGrid>
                )}
            </Spin>

            <Modal
                visible={editField !== null}
                title={editField ? editTitle[editField] : ''}
                confirmLoading={saving}
                onOk={save}
                onCancel={() => setEditField(null)}
                okText="确认"
                cancelText="取消"
                destroyOnClose
                className="setting-info__edit-modal"
            >
                <Form form={form} layout="vertical">
                    {editField === 'shop_name' && (
                        <Form.Item
                            name="shop_name"
                            label="店铺名"
                            rules={[{ required: true, message: '请输入店铺名' }]}
                        >
                            <Input maxLength={30} placeholder="请输入店铺名" />
                        </Form.Item>
                    )}
                    {editField === 'phone' && (
                        <Form.Item
                            name="phone"
                            label="联系电话"
                            rules={[{ required: true, message: '请输入联系电话' }]}
                        >
                            <Input maxLength={20} placeholder="请输入联系电话" />
                        </Form.Item>
                    )}
                    {editField === 'description' && (
                        <Form.Item name="description" label="店铺简介">
                            <Input.TextArea maxLength={200} rows={4} placeholder="请输入店铺简介" />
                        </Form.Item>
                    )}
                    {editField === 'address' && (
                        <>
                            <Form.Item label="省市区">
                                <div className="setting-info__address-region">
                                    <Cascader
                                        options={regionOptions}
                                        value={regionCodes}
                                        changeOnSelect
                                        onChange={(values: Array<string | number>) => {
                                            setRegionCodes(values.map(String))
                                            setMapPoint(null)
                                        }}
                                        placeholder="请选择省、市、区"
                                        style={{ width: '100%' }}
                                    />
                                    <button
                                        className="setting-info__map-preview"
                                        type="button"
                                        aria-label="在地图上查看或选择店铺地址"
                                        onClick={() => setShowMap(true)}
                                    >
                                        {shopPoint ? <img src={shopMapPreview} alt="店铺位置" /> : <span>地图</span>}
                                        <img className="setting-info__map-pin" src={locationImg} alt="" />
                                    </button>
                                </div>
                            </Form.Item>
                            <Form.Item
                                key={`detailed-address-${addressFieldKey}`}
                                name="detailed_address"
                                label="详细地址"
                                rules={[{ required: true, message: '请输入详细地址' }]}
                            >
                                <Input
                                    maxLength={64}
                                    onChange={() => setMapPoint(null)}
                                    placeholder="如道路、门牌号等"
                                />
                            </Form.Item>
                            <span className="store-setting-page__hint">
                                {mapPoint ? '已从地图选择地址，请确认后保存' : '修改地址后请在地图中重新选择位置'}
                            </span>
                        </>
                    )}
                </Form>
            </Modal>
            <MapPicker
                visible={showMap}
                address={[...getRegionNames(regionCodes), form.getFieldValue('detailed_address')]
                    .filter(Boolean)
                    .join(' ')}
                city={getRegionNames(regionCodes)[1] || getRegionNames(regionCodes)[0] || ''}
                point={mapPoint || shopPoint}
                onCancel={() => {
                    setShowMap(false)
                    setEditField(null)
                }}
                onSelect={selection => {
                    setRegionCodes(selection.regionCodes)
                    form.setFields([{ name: 'detailed_address', value: selection.detailedAddress }])
                    // store 已写入但表单字段未必重渲染，递增 key 强制重挂载以显示新值
                    setAddressFieldKey(key => key + 1)
                    setMapPoint({ longitude: selection.longitude, latitude: selection.latitude })
                    setShowMap(false)
                }}
            />
        </SettingPage>
    )
}

export default ShopInfo
